import { useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  getParamFromUrl,
  setParamToUrl,
  UrlParam,
  UrlParamMap,
} from '../utils/url-param';

import { useWindowEventListener } from './use-window-event-listener';

/**
 * This function groups URL param updates and correctly updates window history.
 */
export const batchedUrlUpdates = (
  updates: [(value: any, replaceHistory?: boolean) => void, any][]
) => {
  unstable_batchedUpdates(() => {
    updates.map(([setter, value], index) => setter(value, index > 0));
  });
};

/* ------------------------------------------------------ */

export const useUrlParam = <T extends keyof UrlParamMap>(
  type: T,
  paramName: string,
  initialValue?: UrlParam<T>
): [UrlParam<T>, (value: UrlParam<T>, replaceHistory?: boolean) => void] => {
  const [value, setValue] = useState(getParamFromUrl(type, paramName));

  const setUrlParam = useCallback(
    (value: UrlParam<T>, replaceHistory?: boolean) => {
      setValue(value);
      setParamToUrl(type, paramName, value, replaceHistory);
    },
    [paramName, type]
  );

  /**
   * Back/forward button press handling.
   */
  useWindowEventListener('popstate', () => {
    setValue(getParamFromUrl(type, paramName));
  });

  /**
   * If intial value was provided and value is undefined, use initial value.
   */
  useEffect(() => {
    if (value === undefined && initialValue !== undefined) {
      setValue(initialValue);
      setParamToUrl(type, paramName, initialValue, true);
    }
  }, [initialValue, paramName, type, value]);

  return [value, setUrlParam];
};
